import React from 'react';
import { CalendarClock } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  SUBSCRIPTION_WARNING_DAYS, extendEnd, formatDay, lastDay, subscriptionStatus, toDayString,
} from '../lib/subscription';

/**
 * Status chip for a vendor's plan — the vendors table and the edit dialog.
 */
export function SubscriptionBadge({ vendor }) {
  const s = subscriptionStatus(vendor);
  const styles = {
    unset: ['bg-gray-100 text-gray-700', 'No plan set'],
    notStarted: ['bg-blue-100 text-blue-700', `Starts ${formatDay(s.startsAt)}`],
    active: ['bg-green-100 text-green-700', `Until ${formatDay(lastDay(s))}`],
    expiring: ['bg-amber-100 text-amber-800', `Ends in ${s.daysLeft} day${s.daysLeft === 1 ? '' : 's'}`],
    expired: ['bg-red-100 text-red-700', `Expired ${formatDay(lastDay(s))}`],
    exempt: ['bg-purple-100 text-purple-700', 'Super admin'],
  };
  const [cls, label] = styles[s.state];
  return (
    <Badge className={`${cls} hover:${cls.split(' ')[0]} whitespace-nowrap`} data-testid={`subscription-${s.state}`}>
      {label}
    </Badge>
  );
}

/**
 * Start/end date inputs for the super admin, with one-tap extensions.
 *
 * `value` holds { subscriptionStart, subscriptionEnd } as "YYYY-MM-DD". The end
 * day is included: a plan ending 31 Oct works all of 31 Oct and stops at
 * midnight. Extending adds to the current end date, or to today when the plan
 * has already lapsed — so renewing a late payer does not back-date their time.
 */
export default function SubscriptionFields({ value, onChange, idPrefix = 'sub' }) {
  const start = value.subscriptionStart || '';
  const end = value.subscriptionEnd || '';
  const set = patch => onChange({ ...value, ...patch });
  const extend = months => set({
    subscriptionStart: start || toDayString(new Date()),
    subscriptionEnd: extendEnd(end, months),
  });
  const invalid = start && end && end < start;

  return (
    <div className="border-t border-gray-200 pt-4 space-y-3" data-testid="subscription-fields">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarClock size={15} className="text-purple-500" />
          <Label className="text-sm font-semibold text-gray-700">Subscription</Label>
        </div>
        <SubscriptionBadge vendor={value} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor={`${idPrefix}-start`} className="text-xs">Start date</Label>
          <Input id={`${idPrefix}-start`} type="date" value={start}
            onChange={e => set({ subscriptionStart: e.target.value })} />
        </div>
        <div>
          <Label htmlFor={`${idPrefix}-end`} className="text-xs">End date (last day)</Label>
          <Input id={`${idPrefix}-end`} type="date" value={end} min={start || undefined}
            onChange={e => set({ subscriptionEnd: e.target.value })} />
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {[[1, '+1 month'], [3, '+3 months'], [6, '+6 months'], [12, '+1 year']].map(([m, label]) => (
          <Button key={m} type="button" variant="outline" size="sm" onClick={() => extend(m)}
            className="border-purple-200 text-purple-700 hover:bg-purple-50">
            {label}
          </Button>
        ))}
        {(start || end) && (
          <Button type="button" variant="ghost" size="sm" className="text-gray-500"
            onClick={() => set({ subscriptionStart: '', subscriptionEnd: '' })}>
            Clear
          </Button>
        )}
      </div>
      {invalid && <p className="text-xs text-red-600">The end date is before the start date.</p>}
      <p className="text-xs text-gray-500">
        The kiosk warns the clinic in the last {SUBSCRIPTION_WARNING_DAYS} days, then signs them out
        and blocks login after the end date. With no end date set, the clinic is not limited.
      </p>
    </div>
  );
}
